import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ApiService from "../../services/api.service";
import PlayerModal from "./PlayerModal";
import CoachTeamsPage from "./CoachTeamsPage";
import TeamLogoPicker from "../../components/shared/TeamLogoPicker";
import "../client/ClientLayout.css";
import "./CoachPages.css";
import CreateTeamModal from "./CreateTeamModal";

function age(dob) {
  if (!dob) return null;
  const birth = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getUTCFullYear() - birth.getUTCFullYear();
  const hadBirthday =
    now.getUTCMonth() > birth.getUTCMonth() ||
    (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() >= birth.getUTCDate());
  if (!hadBirthday) years -= 1;
  return years;
}

// ONE screen for two different situations, because the client cannot tell
// them apart: a coach whose league hasn't created their team yet, and a coach
// invited to BRING their own team. The signal for the second is
// Users.invitedByLeagueId, and /me/profile does not project it. Rather than
// guess, offer both readings and the action that serves either.
function WaitingForTeam({ onCreate }) {
  return (
    <div className="coach-name-team-gate">
      <h1 className="client-h1">No team yet</h1>
      <p className="client-subtitle">
        If your league is setting your team up, it'll appear here. Or create your own —
        you keep the roster, and any league can add it by hashtag.
      </p>
      {onCreate && (
        <button className="client-button" onClick={onCreate}>Create My Team</button>
      )}
    </div>
  );
}

// Dispatcher + roster, mirroring iOS's CoachRootScreen (dispatch) +
// CoachTeamsScreen + CoachPlayersScreen split (2026-08-03, multi-team
// support). Rendered at two routes (App.js): the `/coach` index (no
// :teamId — single-team roster, or the team list once there are 2+) and
// `/coach/teams/:teamId` (an explicit team, reached from CoachTeamsPage's
// cards).
export default function CoachPlayersPage() {
  const { teamId: routeTeamId } = useParams();
  const [teams, setTeams] = useState(null); // null = not yet loaded
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, player = edit
  const [showCreateTeam, setShowCreateTeam] = useState(false);

  // The team this instance shows a roster for: the route param when
  // present (pushed from CoachTeamsPage), else the coach's sole team when
  // there's exactly one (the `/coach` index route with no :teamId).
  const team = routeTeamId
    ? teams?.find((t) => t.teamId === routeTeamId) || null
    : teams && teams.length === 1 ? teams[0] : null;

  const loadPlayers = async (teamIdToLoad) => {
    const playersResp = await ApiService.getCoachPlayers(teamIdToLoad);
    setPlayers(playersResp.players || []);
  };

  // Presign -> PUT the bytes -> persist logoUrl -> reload teams so the
  // header's TeamLogoPicker (and CoachTeamsPage's list, once the coach
  // navigates back) pick up the new logoUrl. Mirrors iOS's
  // CoachPlayersViewModel.uploadTeamLogo — same two-endpoint shape.
  const uploadTeamLogo = async (file) => {
    const { url, imageUrl } = await ApiService.getCoachTeamLogoUploadUrl(team.teamId);
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "image/jpeg" },
      body: file,
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    await ApiService.updateCoachTeam(team.teamId, { logoUrl: imageUrl });
    await load();
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const teamsResp = await ApiService.getCoachTeams();
      const list = teamsResp.teams || [];
      setTeams(list);
      const resolvedTeamId = routeTeamId || (list.length === 1 ? list[0].teamId : null);
      if (resolvedTeamId) {
        await loadPlayers(resolvedTeamId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTeamId]);

  if (loading) {
    return <div className="client-loading-row">Loading…</div>;
  }

  if (!teams || teams.length === 0) {
    return (
      <>
        <WaitingForTeam onCreate={() => setShowCreateTeam(true)} />
        {showCreateTeam && (
          <CreateTeamModal
            onClose={() => setShowCreateTeam(false)}
            onCreated={() => { setShowCreateTeam(false); load(); }}
          />
        )}
      </>
    );
  }

  // `/coach` with no :teamId and 2+ teams -> the team list, not a roster.
  if (!routeTeamId && teams.length > 1) {
    return <CoachTeamsPage teams={teams} onCreated={load} />;
  }

  if (!team) {
    // :teamId in the URL doesn't match one of this coach's own teams.
    return <WaitingForTeam />;
  }

  const sorted = [...players].sort((a, b) =>
    (a.number || "").localeCompare(b.number || "", undefined, { numeric: true })
  );

  return (
    <div>
      {routeTeamId && <Link to="/coach" className="coach-back-link">← My Teams</Link>}
      <div className="coach-page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <TeamLogoPicker name={team.teamName} logoUrl={team.logoUrl} onUpload={uploadTeamLogo} />
          <div>
            <h1 className="client-h1">{team.teamName || "My Team"}</h1>
            <p className="client-subtitle">
              {team.leagueName ? `${team.leagueName} · ` : ""}
              {players.length} player{players.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <button className="client-button" onClick={() => setEditing({})}>+ Add Player</button>
      </div>

      {sorted.length === 0 ? (
        <div className="client-empty">No players yet. Add your first one.</div>
      ) : (
        <div className="coach-roster-list">
          {sorted.map((p) => (
            // Absolute path — this page renders at two different routes
            // (`/coach` and `/coach/teams/:teamId`), and `players/:playerId`
            // is only ever nested directly under `/coach`.
            <Link key={p.playerId} to={`/coach/players/${p.playerId}`} className="coach-roster-row">
              <span className="coach-roster-number">{p.number || "—"}</span>
              <span className="coach-roster-name">
                {`${p.firstName || ""} ${p.lastName || ""}`.trim() || "Unnamed"}
              </span>
              <span className="coach-roster-meta">
                {p.position ? `${p.position} · ` : ""}
                {age(p.dob) != null ? `Age ${age(p.dob)}` : ""}
              </span>
            </Link>
          ))}
        </div>
      )}

      {error && <div className="client-error">{error}</div>}

      {editing !== null && (
        <PlayerModal
          player={editing.playerId ? editing : null}
          teamId={team.teamId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadPlayers(team.teamId); }}
          onArchived={() => { setEditing(null); loadPlayers(team.teamId); }}
        />
      )}
    </div>
  );
}
